/**
 * FEAT-006 — the booking grid.
 *
 * These constants are shared rather than duplicated because the client snaps a
 * drag to them and the server rejects anything outside them. Two copies would
 * drift, and the visible symptom would be a user dragging a slot the grid drew
 * and being told it is invalid.
 */

/** The building's timezone. Fixed by the FRS, not a per-user preference. */
export const BUILDING_TIMEZONE = 'Asia/Riyadh';

/**
 * Saudi Arabia is UTC+3 and has never observed daylight saving. Written as a
 * constant offset because the `booking_date` generated column needs an IMMUTABLE
 * expression and cannot use the zone name — see the Phase 4 migration.
 */
export const BUILDING_UTC_OFFSET = '+03:00';

/** 07:00–21:00, as the prototype's grid has it. */
export const GRID_START_HOUR = 7;
export const GRID_END_HOUR = 21;

/** Drag-created times snap to half-hour boundaries (REQ-037). */
export const SLOT_MINUTES = 30;

/** Row height in pixels, so the grid and its events agree on scale. */
export const GRID_ROW_HEIGHT = 52;

export const GRID_START_MINUTE = GRID_START_HOUR * 60;
export const GRID_END_MINUTE = GRID_END_HOUR * 60;

/** Minutes past midnight from `HH:MM`. */
export function minutesOfDay(hhmm: string): number {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/** `HH:MM` from minutes past midnight, clamped to the day. */
export function clockTime(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
  const hours = Math.floor(clamped / 60);
  return `${String(hours).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

/** Rounds to the nearest slot boundary (REQ-037). */
export function snapToSlot(minutes: number): number {
  return Math.round(minutes / SLOT_MINUTES) * SLOT_MINUTES;
}

/**
 * The wall-clock minute-of-day an instant falls on in the building's timezone.
 *
 * Used by both sides to decide whether a booking sits inside the grid. Doing it
 * from the formatted local time rather than from the UTC hours is the whole
 * point: `getHours()` answers for wherever the code happens to be running.
 */
export function buildingMinutesOfDay(at: Date): number {
  const hhmm = at.toLocaleTimeString('en-GB', {
    timeZone: BUILDING_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return minutesOfDay(hhmm);
}

/** The calendar date an instant falls on in the building's timezone, `YYYY-MM-DD`. */
export function buildingDate(at: Date): string {
  return at.toLocaleDateString('en-CA', { timeZone: BUILDING_TIMEZONE });
}

/**
 * Whether a slot is one the grid can draw: inside the window, on one day, and
 * ending after it starts.
 *
 * "On one day" is not redundant with the window. A booking from 20:00 to 08:00
 * the next morning has both endpoints inside 07:00–21:00 and is still not a
 * thing the day grid can render — the FRS rejects cross-midnight explicitly.
 */
export function isWithinGrid(startsAt: Date, endsAt: Date): boolean {
  if (endsAt <= startsAt) return false;
  if (buildingDate(startsAt) !== buildingDate(endsAt)) return false;

  const start = buildingMinutesOfDay(startsAt);
  const end = buildingMinutesOfDay(endsAt);

  return start >= GRID_START_MINUTE && end <= GRID_END_MINUTE;
}
